import { selectCurrentUser } from "@/services/slices/authSlice";
import { checkIfUserHasRoles, isSuperAdmin } from "@/helpers/UserHelpers";
import { useDestroyUserMutation, useGetUserQuery } from "@/services/users";
import {
  faArrowLeft,
  faCalendarCheck,
  faEdit,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { ElementRef, FC, useRef } from "react";
import { Card } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ToastContentProps, toast } from "react-toastify";
import AreYouSurToContinueModal from "../modals/AreYouSurToContinueModal";
import AvailabilityModal from "../modals/availability/AvailabilityModal";
import IconButton from "@/components/atoms/IconButton";
import Flex from "@/components/atoms/Flex";
import { CardDetailsHeaderProps } from "@/interfaces/Card";

const UserHeader: FC<CardDetailsHeaderProps> = ({ id, isEdit }) => {
  const DeleteModalRef =
    useRef<ElementRef<typeof AreYouSurToContinueModal>>(null);
  const user = useSelector(selectCurrentUser);
  const navigation = useNavigate();
  const [deleteUserAsync] = useDestroyUserMutation();
  const AvailabilityModalRef =
    useRef<ElementRef<typeof AvailabilityModal>>(null);
  const { data: target } = useGetUserQuery(id);
  // Chacun les siennes ; ADMIN et CSM celles de tous (spec § 2.6).
  const canManageAccount = !isSuperAdmin(target) || isSuperAdmin(user);
  const canEditAvailability =
    user?.id === id || checkIfUserHasRoles(user, ["ADMIN", "CSM"]);

  const onDelete = (id: string) => {
    toast.promise(deleteUserAsync(id).unwrap(), {
      pending: "Suppression de l'utilisateur en cours...",
      success: {
        render() {
          DeleteModalRef.current?.showOrHiddenModal();
          navigation("/users");
          return (
            <p style={{ marginBottom: 0, textAlign: "center" }}>
              L'utilisateur à bien été supprimée 🤩
            </p>
          );
        },
      },
      error: {
        render({ data }: ToastContentProps<FetchBaseQueryError>) {
          if (data?.status === 422) {
            return "Les champs que vous avez remplis semblent être incorrects.";
          }

          return "Une erreur est survenue";
        },
      },
    });
  };

  return (
    <Card className="mb-3">
      <AreYouSurToContinueModal
        ref={DeleteModalRef}
        onContinue={onDelete}
        title="Souhaitez-vous vraiment supprimé l'utilisateur qui a pour id : {{id}} ?"
      />
      <AvailabilityModal
        ref={AvailabilityModalRef}
        userId={id}
        title={
          target ? `Disponibilités de ${target.fullname}` : "Disponibilités"
        }
        readOnly={!canEditAvailability}
      />
      <Card.Header className="d-flex flex-between-center">
        <IconButton
          onClick={() => navigation(-1)}
          variant="nolina-default"
          size="sm"
          icon={faArrowLeft}
        />
        <Flex>
          <div
            className="bg-300 mx-3 d-xl-none"
            style={{ width: "1px", height: "29px" }}
          ></div>
          <IconButton
            onClick={() => AvailabilityModalRef.current?.showOrHiddenModal()}
            variant="nolina-default"
            size="sm"
            icon={faCalendarCheck}
            transform="shrink-2"
            iconAlign="middle"
            className="me-2"
            aria-label="Disponibilités"
          >
            <span className="d-none d-xl-inline-block ms-1">
              Disponibilités
            </span>
          </IconButton>
          {!isEdit && canManageAccount && (
            <IconButton
              href={`/users/${id}/edit`}
              variant="nolina-default"
              size="sm"
              icon={faEdit}
              transform="shrink-2"
              iconAlign="middle"
              className="me-2"
            >
              <span className="d-none d-xl-inline-block ms-1">Modifier</span>
            </IconButton>
          )}
          {user?.id !== id && canManageAccount && (
            <IconButton
              onClick={() => DeleteModalRef.current?.showOrHiddenModal(id)}
              variant="nolina-default"
              size="sm"
              icon={faTrash}
              iconAlign="middle"
              className="d-none d-sm-block me-2"
            >
              <span className="d-none d-xl-inline-block ms-1">Supprimer</span>
            </IconButton>
          )}
        </Flex>
      </Card.Header>
    </Card>
  );
};

export default UserHeader;
